import React, { useEffect, useState } from 'react';

// Shared visual building blocks for the trainer portal (client/src/pages/trainer/*)
// so every page's stat row and card headers get the same icon-badged treatment
// instead of plain text — same color-mix(icon-on-tinted-circle) pattern as
// DashboardPage's StatCard. Only the icon glyph carries the accent color; the
// actual readable text always stays on var(--sms-txt)/var(--sms-txt2), which
// are the palette's high-contrast tokens (documented ratios in index.css), so
// this stays WCAG AA-safe in both themes without re-deriving contrast per page.

function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

// Counts up from 0 to `target` on mount/change — same eased rAF approach as
// DashboardPage's stat cards, so the trainer portal's numbers get the same
// "arriving" feel instead of just popping in static.
function useCountUp(target, duration = 900) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    const num = Number(target);
    if (!isFinite(num)) { setValue(0); return; }
    if (prefersReducedMotion()) { setValue(num); return; }
    let raf, start;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(num * eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return value;
}

// Icon-badged stat card for a trn-stat-row. Numeric `value`s count up on
// mount/change; pass `decimals` for a fractional stat (e.g. weekly hours).
// Non-numeric values (badges, '—') render as-is, no animation. The card gets
// a faint top-down tint of its own accent color (same recipe as the admin
// dashboard's StatCard gradient) plus a top accent bar, so the row reads as
// five distinct colored signals instead of five identical white boxes —
// text itself stays on var(--sms-txt)/var(--sms-txt2) so contrast never
// depends on the tint.
export function KpiCard({ label, value, icon, color, caption, decimals = 0, live = false, onClick }) {
  const isNumeric = typeof value === 'number';
  const animated = useCountUp(isNumeric ? value : 0);
  const display = isNumeric ? animated.toFixed(decimals) : value;
  const clickable = typeof onClick === 'function';
  return (
    <div className="trn-stat-card" role={clickable ? 'button' : undefined} tabIndex={clickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={clickable ? (e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e); } }) : undefined}
      style={{
        position: 'relative', overflow: 'hidden',
        background: `radial-gradient(110% 120% at 100% 0%, color-mix(in srgb, ${color} 13%, transparent), transparent 62%), var(--sms-sur)`,
        cursor: clickable ? 'pointer' : undefined,
      }}>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <span className="trn-stat-label d-flex align-items-center gap-1" style={{ marginBottom: 0 }}>
          {label}
          {live && <span style={{ color }}><span className="sms-live-pulse" /></span>}
        </span>
        <div className="trn-kpi-icon" style={{ width: 38, height: 38, borderRadius: 11, background: `color-mix(in srgb, ${color} 18%, transparent)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <i className={`bi ${icon}`} style={{ color, fontSize: 18 }} />
        </div>
      </div>
      <div className="trn-stat-value" style={{ marginTop: 0 }}>{display}</div>
      {caption && <div className="trn-stat-caption">{caption}</div>}
    </div>
  );
}

// Small icon-badge placed before a card's <h3> title, replacing a bare
// Bootstrap icon glyph sitting inline with the text.
export function SectionIcon({ icon, color }) {
  return (
    <span style={{
      width: 26, height: 26, borderRadius: 7, marginRight: 8, flexShrink: 0,
      background: `color-mix(in srgb, ${color} 12%, transparent)`,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <i className={`bi ${icon}`} style={{ color, fontSize: 13 }} />
    </span>
  );
}

// Ring/donut chart built from stacked SVG arcs (no charting library) — used
// by the trainer dashboard to show a status breakdown (e.g. claims by
// status) as a shape instead of another number list. Segments fade+grow in
// on mount, staggered, via .trn-donut-seg (see index.css). `segments` is
// [{ key, label, value, color }]; only entries with value > 0 render.
export function DonutChart({ segments, centerLabel, centerValue, size = 132, strokeWidth = 15 }) {
  const total = segments.reduce((s, seg) => s + (seg.value || 0), 0);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  let acc = 0;
  const visible = segments.filter(s => s.value > 0);
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="var(--sms-sur2)" strokeWidth={strokeWidth} />
        {total > 0 && visible.map((seg, i) => {
          const frac = seg.value / total;
          const dash = frac * circumference;
          const dashoffset = circumference - acc;
          acc += dash;
          return (
            <circle key={seg.key} className="trn-donut-seg" cx={size / 2} cy={size / 2} r={radius} fill="none"
              stroke={seg.color} strokeWidth={strokeWidth} strokeDasharray={`${dash} ${circumference - dash}`}
              strokeDashoffset={dashoffset} style={{ animationDelay: `${i * 0.09}s` }} />
          );
        })}
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '0 12px' }}>
        <span style={{ fontSize: 20, fontWeight: 800, color: 'var(--sms-txt)', lineHeight: 1 }}>{centerValue}</span>
        {centerLabel && <span style={{ fontSize: 12, color: 'var(--sms-txt2)', marginTop: 3 }}>{centerLabel}</span>}
      </div>
    </div>
  );
}

// Dot + count legend to sit beside a DonutChart.
export function DonutLegend({ segments }) {
  const total = segments.reduce((s, seg) => s + (seg.value || 0), 0);
  return (
    <div className="d-flex flex-column gap-2">
      {segments.filter(s => s.value > 0).map(s => (
        <div key={s.key} className="d-flex align-items-center gap-2" style={{ fontSize: 12.5 }}>
          <span style={{ width: 9, height: 9, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
          <span style={{ color: 'var(--sms-txt2)', minWidth: 78 }}>{s.label}</span>
          <span style={{ fontWeight: 700, color: 'var(--sms-txt)', fontVariantNumeric: 'tabular-nums' }}>{s.value}</span>
          {total > 0 && <span style={{ color: 'var(--sms-txt2)', fontSize: 12 }}>({Math.round(s.value / total * 100)}%)</span>}
        </div>
      ))}
    </div>
  );
}

// Friendlier empty state for the trainer portal: icon on a soft tinted disc
// instead of a bare glyph, so empty cards still feel designed.
export function TrnEmpty({ icon = 'bi-inbox', message = 'Nothing here', color = 'var(--sms-acc)' }) {
  return (
    <div className="trn-empty-state" style={{ '--accent': color }}>
      <span className="trn-empty-disc"><i className={`bi ${icon}`} /></span>
      <p>{message}</p>
    </div>
  );
}
